/**
 * @module features/auth/pages/LoginPage
 * @description Dedicated Super Admin Login screen adhering to the elevated minimalist Folk/Elera design language.
 * Clean, distraction-free canvas with zero cross-portal link leakage.
 */

import * as React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { LoginForm } from "../components/LoginForm";
import { useAuth } from "../hooks/useAuth";
import { UserDto } from "@/types/auth";
import { APP_NAME } from "@/config/env";

export function LoginPage() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  const toggleTheme = React.useCallback(() => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);
  }, []);

  const from = (location.state as any)?.from?.pathname || "/s-admin/users";

  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, from]);

  const handleSuccess = (user: UserDto) => {
    login(user);
    navigate(from, { replace: true });
  };

  const brandDisplay = APP_NAME.toLowerCase();

  return (
    <main className="min-h-dvh w-full flex flex-col justify-between bg-background dark:bg-zinc-950 text-foreground selection:bg-zinc-200 dark:selection:bg-zinc-800 relative px-6 py-6 sm:px-12 sm:py-8 select-none">
      {/* Top Header: Brand Wordmark (top-left) & Theme Switcher (top-right) */}
      <header className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2 select-none">
          {/* Geometric circular brand emblem */}
          <div className="h-6 w-6 rounded-full border-[2.5px] border-foreground flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-foreground" />
          </div>
          <span className="text-xl sm:text-2xl font-bold tracking-tight text-foreground font-sans">
            {brandDisplay}.
          </span>
        </div>

        {/* Minimal theme toggle button */}
        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Sun className="h-4 w-4 text-amber-400" />
          ) : (
            <Moon className="h-4 w-4 text-zinc-600" />
          )}
        </button>
      </header>

      {/* Main Form Content */}
      <div className="w-full flex flex-col items-center justify-center py-10 my-auto">
        <div className="w-full max-w-[340px] sm:max-w-[640px] md:max-w-[680px] flex flex-col">
          <LoginForm onSuccess={handleSuccess} />
        </div>
      </div>

      {/* Bottom Legal Terms Footer */}
      <footer className="w-full pb-2 text-center">
        <p className="text-[11px] text-muted-foreground max-w-sm mx-auto leading-relaxed">
          By signing in, you agree to our{" "}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Privacy policy
          </Link>
          .
        </p>
      </footer>
    </main>
  );
}
